import React, { useEffect } from "react";
import {
  Modal, Form, Input, InputNumber, Select, Button, Row, Col, Tag, Typography,
} from "antd";
import { UserOutlined, PhoneOutlined, MailOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import type { VenueEvent } from "../../../../utils/api/venueEventsAPI";
import type { Reservation } from "../../../../utils/api/reservationsAPI";

const { Text } = Typography;

type Props = {
  open: boolean;
  event: VenueEvent;
  editData?: Reservation | null;
  onClose: () => void;
  onSave: (values: any) => Promise<void>;
  loading?: boolean;
  initialTableNumber?: number | null;
  takenTableNumbers?: number[];
};

const AddEventReservationModal: React.FC<Props> = ({
  open, event, editData, onClose, onSave, loading,
  initialTableNumber, takenTableNumbers = [],
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const isEdit = !!editData;

  const available = event.totalCapacity - event.reservedCount + (editData?.ticketsCount || 0);

  useEffect(() => {
    if (open) {
      if (editData) {
        form.setFieldsValue({
          guestName: editData.guestName,
          guestPhone: editData.guestPhone,
          guestEmail: editData.guestEmail || "",
          ticketsCount: editData.ticketsCount || 1,
          status: editData.status,
          paymentStatus: editData.paymentStatus || "unpaid",
          amountPaid: editData.amountPaid || 0,
          note: editData.note || "",
          eventTableNumber: editData.eventTableNumber || undefined,
        });
      } else {
        form.resetFields();
        form.setFieldsValue({
          status: "confirmed",
          paymentStatus: "unpaid",
          source: "internal",
          ticketsCount: 1,
          amountPaid: 0,
          eventTableNumber: initialTableNumber || undefined,
        });
      }
    }
  }, [open, editData?._id, initialTableNumber]);

  const handleAmountPaidChange = (val: number | null) => {
    if (!event.ticketPrice) return;
    const tickets = form.getFieldValue("ticketsCount") || 1;
    const total = event.ticketPrice * tickets;
    const paid = val || 0;
    if (paid <= 0) form.setFieldValue("paymentStatus", "unpaid");
    else if (paid >= total) form.setFieldValue("paymentStatus", "paid");
    else form.setFieldValue("paymentStatus", "partially_paid");
  };

  const handleTicketsChange = (val: number | null) => {
    if (!event.ticketPrice) return;
    const amountPaid = form.getFieldValue("amountPaid") || 0;
    const total = event.ticketPrice * (val || 1);
    if (amountPaid <= 0) form.setFieldValue("paymentStatus", "unpaid");
    else if (amountPaid >= total) form.setFieldValue("paymentStatus", "paid");
    else form.setFieldValue("paymentStatus", "partially_paid");
  };

  const onFinish = async (values: any) => {
    await onSave({
      ...values,
      date: event.date,
      time: event.time,
      numberOfGuests: values.ticketsCount,
      eventTableNumber: values.eventTableNumber || null,
    });
    form.resetFields();
  };

  const hasTables = event.totalTables != null && event.totalTables > 0;

  return (
    <Modal
      title={
        <div>
          <div style={{ fontWeight: 600 }}>{isEdit ? t("Edit Reservation") : t("Add Reservation")}</div>
          <Text type="secondary" style={{ fontSize: 13, fontWeight: 400 }}>{event.title}</Text>
        </div>
      }
      open={open}
      onCancel={onClose}
      footer={null}
      width={480}
      destroyOnClose
    >
      <div style={{ marginBottom: 12, display: "flex", gap: 8, flexWrap: "wrap" }}>
        <Tag color="blue">{event.date?.slice(0, 10)} {event.time}</Tag>
        {!isEdit && (
          <Tag color={available <= 5 ? "orange" : "green"}>
            {available} {t("spots available")}
          </Tag>
        )}
        {event.ticketPrice > 0 && (
          <Tag>{event.ticketPrice} {event.currency} {t("per ticket")}</Tag>
        )}
      </div>

      <Form form={form} layout="vertical" onFinish={onFinish} requiredMark={false}>
        <Form.Item name="guestName" label={t("Guest Name")} rules={[{ required: true, message: t("Required") }]}>
          <Input prefix={<UserOutlined />} placeholder={t("Full name")} size="large" />
        </Form.Item>

        <Row gutter={12}>
          <Col xs={24} sm={12}>
            <Form.Item name="guestPhone" label={t("Phone")} rules={[{ required: true, message: t("Required") }]}>
              <Input prefix={<PhoneOutlined />} placeholder="+387 61 123 456" size="large" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name="guestEmail" label={t("Email (optional)")} rules={[{ type: "email", message: t("Invalid email") }]}>
              <Input prefix={<MailOutlined />} placeholder="email@example.com" size="large" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={12}>
          <Col xs={24} sm={hasTables ? 8 : 12}>
            <Form.Item name="ticketsCount" label={t("Tickets")} rules={[{ required: true, message: t("Required") }]}>
              <InputNumber min={1} max={available} size="large" style={{ width: "100%" }} onChange={handleTicketsChange} />
            </Form.Item>
          </Col>
          {hasTables && (
            <Col xs={24} sm={8}>
              <Form.Item name="eventTableNumber" label={t("Table")}>
                <Select size="large" placeholder={t("No table")} allowClear>
                  {Array.from({ length: event.totalTables! }, (_, i) => i + 1).map((num) => {
                    const isTaken = takenTableNumbers.includes(num) &&
                      !(editData?.eventTableNumber === num);
                    return (
                      <Select.Option key={num} value={num} disabled={isTaken}>
                        <span>{t("Table")} {num}</span>
                        {isTaken && (
                          <Tag color="red" style={{ marginLeft: 6, fontSize: 11 }}>{t("Taken")}</Tag>
                        )}
                      </Select.Option>
                    );
                  })}
                </Select>
              </Form.Item>
            </Col>
          )}
          <Col xs={24} sm={hasTables ? 8 : 12}>
            <Form.Item name="status" label={t("Status")}>
              <Select size="large">
                <Select.Option value="pending">{t("Pending")}</Select.Option>
                <Select.Option value="confirmed">{t("Confirmed")}</Select.Option>
                <Select.Option value="paid">{t("Paid")}</Select.Option>
                <Select.Option value="waitlisted">{t("Waitlisted")}</Select.Option>
                <Select.Option value="cancelled">{t("Cancelled")}</Select.Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={12}>
          <Col xs={24} sm={12}>
            <Form.Item name="paymentStatus" label={t("Payment")}>
              <Select size="large">
                <Select.Option value="unpaid">{t("Unpaid")}</Select.Option>
                <Select.Option value="partially_paid">{t("Partial")}</Select.Option>
                <Select.Option value="paid">{t("Paid")}</Select.Option>
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name="amountPaid" label={t("Amount Paid")}>
              <InputNumber
                min={0} size="large" style={{ width: "100%" }}
                addonAfter={event.currency} onChange={handleAmountPaidChange}
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item name="note" label={t("Note")}>
          <Input.TextArea rows={2} placeholder={t("Any special requests...")} maxLength={300} />
        </Form.Item>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <Button size="large" onClick={onClose}>{t("Cancel")}</Button>
          <Button type="primary" size="large" htmlType="submit" loading={loading}>
            {isEdit ? t("Save Changes") : t("Add Reservation")}
          </Button>
        </div>
      </Form>
    </Modal>
  );
};

export default AddEventReservationModal;
